import db from "../../../../db/database";
import { COUNTRY_CODES } from "../../../../constants/countries";
import { getVatByProductAndClient } from "../../../../services/salesPriceService";
import { revisarIvaDelCliente } from "./validacionDeHacienda";

/**
 * La guarda de IVA del cliente, con los datos resueltos desde la base local.
 *
 * `revisarIvaDelCliente` es la regla y recibe todo hecho. Esto le busca lo que
 * falta: la ficha del cliente, la tienda de la caja y su cliente por defecto, y
 * los productos. La usan la sincronización --como `DATOS_INVALIDOS`, así que la
 * venta no sale y queda editable-- y la pantalla de la venta, como aviso al
 * elegir el cliente. Una sola resolución para los dos, para que no digan cosas
 * distintas. Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`, fila H3 · TPV.
 */

const texto = (valor) => String(valor ?? "").trim();

const vivo = (registro) => !registro?.deleted;

/** La primera fila viva de `tabla` con ese `No`, o `null`. */
const porNo = async (tabla, no) => {
  const buscado = texto(no);
  if (!buscado) return null;
  return (await db[tabla].where("No").equals(buscado).and(vivo).first()) || null;
};

/**
 * La ficha del cliente de un documento.
 *
 * `Sell_to_Customer_No_` guarda el `No` de BC o, mientras el cliente no llega, su
 * id local; `clientLocalId` es el id local. El mismo orden que `getClientByNoOrId`.
 */
const fichaDelDocumento = async (documento) => {
  for (const clave of [documento?.Sell_to_Customer_No_, documento?.clientLocalId]) {
    if (clave === null || clave === undefined || clave === "") continue;
    const porId = Number.isInteger(Number(clave)) ? await db.clients.get(Number(clave)) : null;
    if (porId && vivo(porId)) return porId;
    const ficha = await porNo("clients", clave);
    if (ficha) return ficha;
  }
  return null;
};

/**
 * El cliente por defecto de la tienda a la que pertenece la caja.
 *
 * La tienda sale de la caja (`cajas.TPV`), y el cliente por defecto de la tienda
 * (`tpvs.defaultCustomer`, que guarda `tpvSyncService` desde `DefaultCustomer`).
 * Es el mismo camino que sigue la venta en pantalla para el tipo de comprobante.
 *
 * @param {string} cajaNo
 * @returns {Promise<{tienda: string, noDelCliente: string, ficha: object|null}>}
 */
export const clientePorDefectoDeLaCaja = async (cajaNo) => {
  const caja = await porNo("cajas", cajaNo);
  const tienda = texto(caja?.TPV);
  const registroDeTienda = tienda ? await porNo("tpvs", tienda) : null;
  const noDelCliente = texto(registroDeTienda?.defaultCustomer);
  const ficha = noDelCliente ? await porNo("clients", noDelCliente) : null;

  return { tienda, noDelCliente, ficha };
};

/** Por qué no se pudo comparar, para la consola de sincronización. */
const motivoSinReferencia = ({ cajaNo, tienda, noDelCliente }) => {
  if (!tienda) {
    return `la caja ${cajaNo || "(sin caja)"} no tiene tienda en este equipo; no se revisó el IVA del cliente contra el de mostrador`;
  }
  if (!noDelCliente) {
    return `la tienda ${tienda} no tiene cliente por defecto (DefaultCustomer); no se revisó el IVA del cliente contra el de mostrador`;
  }
  return `el cliente por defecto ${noDelCliente} de la tienda ${tienda} no está en este equipo; no se revisó el IVA del cliente contra el de mostrador`;
};

/**
 * Lo que Hacienda rechazaría por el IVA del cliente, resuelto desde Dexie.
 *
 * @param {object} args
 * @param {object} args.documento  La cabecera: `caja`, `Document_Type` y el cliente.
 * @param {Array} args.lineas  Las líneas del documento.
 * @param {string} args.countryCode  El país activo.
 * @param {object} [args.ficha]  El cliente, si quien llama ya lo tiene; si no, se busca.
 * @param {Array} [args.productos]  El catálogo en memoria, si quien llama ya lo tiene.
 * @returns {Promise<{avisos: Array<{campo: string, aviso: string, gravedad: string}>, sinClientePorDefecto: boolean, motivo: string|null}>}
 *   `motivo` sólo viene con `sinClientePorDefecto`, listo para `anotarAviso`.
 */
export const revisarIvaDelDocumento = async ({
  documento,
  lineas,
  countryCode,
  ficha,
  productos,
} = {}) => {
  const nada = { avisos: [], sinClientePorDefecto: false, motivo: null };

  // El país y el tipo se miran antes de tocar la base: por aquí pasan los seis
  // países, y a los otros cinco esto no les cuesta ni una consulta.
  if (countryCode !== COUNTRY_CODES.COSTARICA) return nada;
  if (documento?.Document_Type === "Credit Memo") return nada;
  if (!lineas || lineas.length === 0) return nada;

  // Las dos grafías, como en la sincronización (`invoice.Caja || invoice.caja`).
  const cajaNo = texto(documento?.caja || documento?.Caja);
  const referencia = await clientePorDefectoDeLaCaja(cajaNo);

  const cliente = ficha === undefined ? await fichaDelDocumento(documento) : ficha;

  const buscarProducto = Array.isArray(productos)
    ? async (no) => productos.find((p) => p?.No === no) || null
    : (no) => porNo("products", no);

  const { avisos, sinClientePorDefecto } = await revisarIvaDelCliente({
    lineas,
    cliente,
    clientePorDefecto: referencia.ficha,
    buscarProducto,
    buscarIva: getVatByProductAndClient,
    esNotaDeCredito: false,
    countryCode,
  });

  return {
    avisos,
    sinClientePorDefecto,
    motivo: sinClientePorDefecto ? motivoSinReferencia({ cajaNo, ...referencia }) : null,
  };
};
